

















import { computed, defineComponent, provide, reactive, ref, toRef, watch } from "@vue/composition-api";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import { getProfileUrl } from "@/lib/urlHelpers";
import CallsLogger from "@/modules/callsLogger";
import { PROVIDED_DATA, PROVIDED_DATA_KEY, ROLES } from "@/const";
import EventTicketPurchaseModal from "@/components/EventTicketPurchaseModal";
import { pauseGlobalPlayer } from "@/components/GlobalAudioPlayer/helpers";
import ModalView from "@/components/ModalView/ModalView.vue";
import ConsumeController from "./components/ConsumeController";
import ProduceController from "./components/ProduceController";
import useEvents from "./hooks/useEvents";

export default defineComponent<{ user: UserPublicData }>({
  name: "StreamController",
  components: { ModalView, ProduceController, ConsumeController, EventTicketPurchaseModal },
  props: {
    user: { type: Object as () => UserPublicData, required: true },
  },
  setup(props, { root }) {
    const user = toRef(props, "user");

    const { authUser, userVerificationStatus, isCreator, isAdmin } = useAuthUser();
    const { contentFeed, paymentPackages, getEventsData } = useEvents();

    const openModal = ref(true);
    const showPurchaseModal = ref(false);

    const userId = computed(() => user.value.id);
    const isAuthUserProducer = computed(() => authUser.value && userId.value === authUser.value.id);

    const allowAuthUserProduce = computed(() => {
      return userVerificationStatus.value === UserVerificationStatus.VERIFIED && (isCreator.value || isAdmin.value);
    });

    const allowUserProduce = computed(() => {
      return checkRole(user.value.role, [ROLES.CREATOR, ROLES.ADMIN]);
    });

    const allowEnterStream = computed(() => {
      if (isAuthUserProducer.value) return allowAuthUserProduce.value;
      return allowUserProduce.value;
    });

    const onClose = () => {
      CallsLogger.collectLogs(`[STREAM CONTROLLER] - Close`, { streaming: false });
      root.$router.push(getProfileUrl(user.value));
    };

    const getEvent = useAuthorisedOnly(async (eventId: string) => {
      await getEventsData(eventId);
      openModal.value = false;
      showPurchaseModal.value = true;
    });

    const onErrorHandler = async (errorData: Record<string, any>) => {
      await getEvent(errorData.eventId.toString());
    };

    const onFinishBooking = () => {
      openModal.value = true;
      showPurchaseModal.value = false;
    };

    const provided = reactive<PROVIDED_DATA[PROVIDED_DATA_KEY.OPPONENT_USER]>({ user: user.value });

    watch(user, user => (provided.user = user));
    provide<PROVIDED_DATA[PROVIDED_DATA_KEY.OPPONENT_USER]>(PROVIDED_DATA_KEY.OPPONENT_USER, provided);

    pauseGlobalPlayer();

    return {
      isAuthUserProducer,
      userId,
      openModal,
      contentFeed,
      paymentPackages,
      showPurchaseModal,
      onClose,
      onErrorHandler,
      onFinishBooking,
      allowEnterStream,
    };
  },
  beforeRouteEnter(_, __, next) {
    next((vm: any) => {
      if (!vm.allowEnterStream) {
        vm.$router.replace(getProfileUrl(vm.$props.user));
        return false;
      }
      return true;
    });
  },
});
